import * as React from 'react';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { FormCard } from 'components/forms/FormComponents';
import TextField from 'components/forms/TextField';
import useNeveForm from 'components/forms/NeveForm';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import { signUp, SignUpInput } from 'aws-amplify/auth';
import { PasswordPattern, EmailPattern, HomepageContext } from './Homepage';
import strings from 'strings/strings.json';
import { Headline2 } from 'components/Elements';
import Button from 'components/buttons/Button';
import { TurnstileWidget } from './TurnstileWidget';

type SignUpFormInput = {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
};

export function SignUpForm(): JSX.Element {
    const { setState, setSharedData } = React.useContext(HomepageContext);

    const [error, setError] = React.useState(<></>);
    const [submittingSignIn, setSubmittingSignUp] = React.useState(false);
    const [turnstileToken, setTurnstileToken] = React.useState('');
    const [turnstileError, setTurnstileError] = React.useState('');
    const [turnstileResetSignal, setTurnstileResetSignal] = React.useState(0);
    const resetTurnstile = React.useCallback(() => setTurnstileResetSignal((prev) => prev + 1), []);
    const handleTurnstileTokenChange = React.useCallback((token: string) => setTurnstileToken(token), []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<SignUpFormInput>();

    async function submitSignUp(data: SignUpFormInput) {
        if (!turnstileToken) return;

        setSubmittingSignUp(true);

        const signUpInput: SignUpInput = {
            username: data.email,
            password: data.password,
            options: {
                userAttributes: {
                    email: data.email,
                    given_name: data.firstName,
                    family_name: data.lastName,
                },
                autoSignIn: {
                    enabled: false,
                },
                validationData: {
                    turnstileToken,
                    turnstileAction: 'signup',
                },
                clientMetadata: {
                    turnstileToken,
                    turnstileAction: 'signup',
                },
            },
        };

        try {
            await signUp(signUpInput);
            //Result: User created in pool, email not verified, confirmation status unconfirmed
            setSharedData({
                emailAddress: data.email,
                password: data.password,
            });
            setState('confirmSignUp');
        } catch (error: any) {
            resetTurnstile();
            if (error.name === 'UsernameExistsException') {
                setError(
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.homepage.errors.signUp.usernameExistsException}
                        maxWidth={'100%'}
                        buttonOnSameRow={true}
                        buttonToHelp={{
                            text: strings.homepage.buttons.signIn,
                            aria: strings.homepage.buttons.signIn,
                            action: () => setState('signIn'),
                        }}
                    />,
                );
            } else {
                setError(
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.homepage.errors.signUp.general}
                        maxWidth={'100%'}
                    />,
                );
            }
        } finally {
            setSubmittingSignUp(false);
        }
    }

    return (
        <FormCard onSubmit={handleSubmit(submitSignUp)}>
            <Headline2>{strings.homepage.headlines.signUp}</Headline2>
            <Notification
                title={strings.homepage.signInNotification}
                notificationType={NotificationType.INFO}
                maxWidth={'fit-content'}
                buttonOnSameRow={true}
                buttonToHelp={{
                    text: strings.homepage.buttons.signIn,
                    aria: strings.homepage.buttons.signIn,
                    action: () => setState('signIn'),
                }}
            />

            <TextField
                fieldName={strings.homepage.fields.email}
                labelText={strings.homepage.fields.email}
                inputAria={strings.homepage.fields.email}
                required
                errorMessage={errors.email?.message}
                {...register('email', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.email),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.email, fieldErrorCodes.maxLength),
                    },
                    pattern: {
                        value: EmailPattern,
                        message: strings.homepage.fields.emailPattern,
                    },
                })}
            />
            <TextField
                fieldName={strings.homepage.fields.firstName}
                labelText={strings.homepage.fields.firstName}
                inputAria={strings.homepage.fields.firstName}
                required
                errorMessage={errors.firstName?.message}
                {...register('firstName', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.firstName),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.firstName, fieldErrorCodes.maxLength),
                    },
                })}
            />
            <TextField
                fieldName={strings.homepage.fields.lastName}
                labelText={strings.homepage.fields.lastName}
                inputAria={strings.homepage.fields.lastName}
                required
                errorMessage={errors.lastName?.message}
                {...register('lastName', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.lastName),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.lastName, fieldErrorCodes.maxLength),
                    },
                })}
            />
            <TextField
                fieldName={strings.homepage.fields.password}
                labelText={strings.homepage.fields.password}
                inputAria={strings.homepage.fields.password}
                type="password"
                required
                errorMessage={errors.password?.message}
                {...register('password', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.password),
                    },
                    maxLength: {
                        value: 100,
                        message: createErrorMessage(strings.homepage.fields.password, fieldErrorCodes.maxLength),
                    },
                    pattern: {
                        value: PasswordPattern,
                        message: strings.homepage.fields.passwordPattern,
                    },
                })}
            />
            <TurnstileWidget
                action="signup"
                onTokenChange={handleTurnstileTokenChange}
                onError={setTurnstileError}
                resetSignal={turnstileResetSignal}
            />
            {turnstileError && (
                <Notification
                    title={turnstileError}
                    notificationType={NotificationType.DANGER}
                    maxWidth={'100%'}
                />
            )}
            <Button
                title={strings.homepage.buttons.signUp}
                aria={strings.homepage.buttons.signUp}
                type="submit"
                fullwidth
                submitting={submittingSignIn}
                disabled={!turnstileToken}
            />
            {error && error}
        </FormCard>
    );
}
